import type { UnknownQueryParam } from "@octopusdeploy/portal-routes";
import { useQueryStringParams } from "@octopusdeploy/portal-routes";
import type { ReactNode } from "react";
import React from "react";
import { useParams } from "react-router-dom";
import ReloadableRouteContent from "~/components/ReloadableRoute";
import { useDeepCompareMemo } from "~/hooks/useDeepCompareMemo";
import { useSetChameleonRouteParamsEffect } from "~/inAppMessaging/InAppMessaging";
import { Page } from "../Page";
import type { PageRegistration } from "./PageRegistration";
import { decodeRouteParams } from "./decodeRouteParams";
export interface RoutePageProps<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData, LoaderContext, PageContext> {
    pageRegistration: PageRegistration<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext>;
    loaderContext: LoaderContext;
    pageContext: PageContext;
    renderPageLayout?: (page: ReactNode) => ReactNode;
}
export function RoutePage<RouteParams, QueryParams extends UnknownQueryParam[], LoaderData, LoaderContext, PageContext>({ loaderContext, pageRegistration, pageContext, renderPageLayout, }: RoutePageProps<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext>) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const encodedRouteParams = useParams() as RouteParams;
    const [queryParams, setQueryParams] = useQueryStringParams<QueryParams>(pageRegistration.route.queryParameters);
    useSetChameleonRouteParamsEffect();
    return (<RouteParameterDecoder<RouteParams> routeParams={encodedRouteParams} render={(routeParams) => {
            const page = (<Page<RouteParams, QueryParams, LoaderData, LoaderContext, PageContext> pageRegistration={pageRegistration} routeParams={routeParams} queryParams={queryParams} setQueryParams={setQueryParams} loaderContext={loaderContext} pageContext={pageContext}/>);
            return <ReloadableRouteContent>{renderPageLayout ? renderPageLayout(page) : page}</ReloadableRouteContent>;
        }}/>);
}
interface RouteParameterDecoderProps<RouteParams> {
    routeParams: RouteParams;
    render: (params: RouteParams) => ReactNode;
}
function RouteParameterDecoder<RouteParams>({ routeParams, render }: RouteParameterDecoderProps<RouteParams>) {
    // We get a new instance of routeParams on every render from react router,
    // but we want the routeParams to be stable for any downstream code.
    const decodedParams = useDeepCompareMemo(decodeRouteParams(routeParams));
    return <>{render(decodedParams)}</>;
}
